import { useMemo, useState } from 'react'
import { useDirectoryMutations, useFarmerOrganisations, useFarmers, useSession, useSuppliers } from '../../api/hooks'
import { RecordPicker, type RecordOption } from './RecordPicker'

export type { RecordOption } from './RecordPicker'

interface Common {
  value: RecordOption | null
  onChange: (option: RecordOption | null) => void
  warn?: boolean
  disabled?: boolean
}

/** A supplier on record, or a new one in `countryCode`. */
export function SupplierPicker({ countryCode, ...p }: Common & { countryCode: string | null }) {
  const [q, setQ] = useState('')
  const { data: suppliers = [], isFetching } = useSuppliers()
  const { createSupplier } = useDirectoryMutations()
  // Kept in step from the organisation's own list: chosen from it, not added.
  const synced = useSession().data?.directory === 'synced'
  const options = useMemo(
    () => suppliers
      .filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()))
      .slice(0, 50)
      .map((s) => ({ id: s.id, label: s.name, detail: s.country_code })),
    [suppliers, q],
  )
  return (
    <RecordPicker
      {...p}
      ariaLabel="SU"
      placeholder={synced ? 'Choose a supplier' : 'Choose or add a supplier'}
      options={options}
      loading={isFetching}
      onSearch={setQ}
      onCreate={countryCode && !synced ? async (name) => {
        const s = await createSupplier.mutateAsync({ name, country_code: countryCode })
        return { id: s.id, label: s.name, detail: s.country_code }
      } : undefined}
    />
  )
}

/** A farmer organisation (FO, with its FO_ID), or a new one by name. */
export function FoPicker({ supplierId, countryCode, ...p }: Common & { supplierId?: string | null; countryCode?: string | null }) {
  const [q, setQ] = useState('')
  const { data = [], isFetching } = useFarmerOrganisations({ q, limit: 30 })
  const { createFo } = useDirectoryMutations()
  return (
    <RecordPicker
      {...p}
      ariaLabel="FO"
      placeholder="Choose or add a farmer organisation"
      options={data.map((o) => ({ id: o.id, label: o.name, code: o.code, detail: o.code ? `FO_ID ${o.code}` : 'No FO_ID' }))}
      loading={isFetching}
      onSearch={setQ}
      onCreate={async (name) => {
        const o = await createFo.mutateAsync({ name, country_code: countryCode ?? null, ...(supplierId ? { supplier_id: supplierId } : {}) })
        return { id: o.id, label: o.name, code: o.code, detail: 'No FO_ID' }
      }}
    />
  )
}

/** A farmer by Farmer_ID or name, or a new one with that Farmer_ID. */
export function FarmerPicker({ supplierId, foId, ...p }: Common & { supplierId?: string | null; foId?: string | null }) {
  const [q, setQ] = useState('')
  const { data = [], isFetching } = useFarmers({ q, limit: 30 })
  const { createFarmer } = useDirectoryMutations()
  const label = (f: { code: string | null; name: string | null }) => f.code ?? f.name ?? '—'
  return (
    <RecordPicker
      {...p}
      ariaLabel="Farmer_ID"
      placeholder="Choose or add a farmer"
      options={data.map((f) => ({
        id: f.id, label: label(f), code: f.code,
        detail: [f.code && f.name ? f.name : null, `${f.plot_count} plot${f.plot_count === 1 ? '' : 's'}`].filter(Boolean).join(' · '),
      }))}
      loading={isFetching}
      onSearch={setQ}
      onCreate={async (code) => {
        const f = await createFarmer.mutateAsync({
          code, ...(supplierId ? { supplier_id: supplierId } : {}), ...(foId ? { fo_id: foId } : {}),
        })
        return { id: f.id, label: label(f), code: f.code }
      }}
    />
  )
}
